import type { DICOMMetadataStackValidationReport } from '@dcmctkit/wasm';
import { FileWithPath } from 'react-dropzone';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';

import { ResolvedCbctOrder, VALIDATION_TIMEOUT_MS } from '../config';

// Ordered streaming conversion only applies to multi-file CBCT DICOM uploads.
// A single file (or a single .zip archive) and every other study type upload
// unordered exactly as before.
const canValidateOrder = (
  assetType: AssetType,
  files: FileWithPath[],
): boolean => assetType === AssetType.AssetType_Study_CBCT && files.length > 1;

const runValidator = (
  files: File[],
): Promise<DICOMMetadataStackValidationReport> =>
  new Promise((resolve, reject) => {
    const worker = new Worker(
      new URL('./cbctOrderValidator.worker.ts', import.meta.url),
      { type: 'module' },
    );

    const timeout = setTimeout(() => {
      worker.terminate();
      reject(new Error('CBCT validation timed out'));
    }, VALIDATION_TIMEOUT_MS);

    const settle = (run: () => void) => {
      clearTimeout(timeout);
      worker.terminate();
      run();
    };

    worker.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'result') {
        settle(() => resolve(event.data.report));

        return;
      }

      settle(() =>
        reject(new Error(event.data?.message ?? 'CBCT validation failed')),
      );
    };

    worker.onerror = (event) => {
      settle(() =>
        reject(new Error(event.message || 'CBCT validation worker error')),
      );
    };

    worker.postMessage({ files });
  });

// resolveCbctUploadOrder validates a CBCT DICOM series in the browser (via the
// @dcmctkit/wasm worker) and, when it yields a sound axial order covering every
// selected file, returns the files reordered into slice order with
// `ordered = true` so the server can stream the DICOM->DCCT conversion. On any
// failure — bad status, partial coverage, worker error, or timeout — it falls
// back to the original order with `ordered = false` and a debug `validationError`
// for the server to store. It never drops or reorders away any selected file.
export const resolveCbctUploadOrder = async ({
  assetType,
  files,
}: {
  assetType: AssetType;
  files: FileWithPath[];
}): Promise<ResolvedCbctOrder> => {
  if (!canValidateOrder(assetType, files)) {
    return { files, ordered: false };
  }

  try {
    const report = await runValidator(files);

    if (report.status !== 'strict-ok' && report.status !== 'relaxable-ok') {
      return {
        files,
        ordered: false,
        validationError: `[${report.status}] ${report.issues.join('; ')}`,
      };
    }

    const orderedIndices = report.fileIndicesInAxialOrder;

    // The validator contract does NOT guarantee `fileIndicesInAxialOrder` is a
    // full permutation of the input: with multiple valid CT studies in one
    // folder it silently picks one subset, and it makes no promise about the
    // indices being unique or in range. So verify it here — every index an
    // integer within bounds, used exactly once, covering all selected files —
    // before trusting it to reorder. Anything short of a true permutation would
    // drop or duplicate a file, so fall back to the full unordered set instead.
    const isFullPermutation =
      orderedIndices.length === files.length &&
      new Set(orderedIndices).size === files.length &&
      orderedIndices.every(
        (index) =>
          Number.isInteger(index) && index >= 0 && index < files.length,
      );

    if (!isFullPermutation) {
      return {
        files,
        ordered: false,
        validationError: `[${report.status}] validated order is not a full permutation of ${files.length} files (got ${orderedIndices.length} indices); uploading unordered`,
      };
    }

    const orderedFiles = orderedIndices.map((index) => files[index]);

    return { files: orderedFiles, ordered: true };
  } catch (error) {
    return {
      files,
      ordered: false,
      validationError: error instanceof Error ? error.message : String(error),
    };
  }
};
